import {
    withMainControls,
    WithMainControlsProps,
} from '@eventbrite/eds-structure';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { $FixMe } from '@eventbrite/ts-utils'; /* eslint-disable react/jsx-handler-names */
import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { RenderOnViewport } from '../../components/RenderOnViewport';
import {
    logSearchMetrics,
    trackSearchPageView,
} from '../../redux/actions/analytics';
import {
    DiscoverState,
    EventSearch,
    PaginationData,
    ThingsToDoShelfState,
} from '../../types';
import { getSearchSection } from '../../utils/general';
import { LazyMapPanelContainer } from './containers/MapPanelContainer';
import { SearchBasedRecommendedEvents } from './containers/SearchRelatedEvents/SearchBasedRecommendedEvents';
import { SearchResultsPanelContainer } from './containers/SearchResultsPanelContainer';
import styles from './SearchPageContent.module.scss';
import {
    useErrorHandlingEffects,
    useEventSearchSyncEffects,
    useSearchBaseTrackingEffects,
} from './SearchPageContentEffects';
import './SearchPageContentOverrides.scss';
import {
    SearchCachedData,
    SearchDataParams,
    useSearchData,
} from './SearchPageData';

type SearchPageContentReduxProps = RouteComponentProps &
    WithMainControlsProps & {
        locale: string;
        eventSearch: EventSearch;
        dispatch: Function;
        searchContext: SearchDataParams['searchContext'];
        /**
         * GA Utility for tracking the pageview of search
         */
        onTrackPageView: (
            events: FormattedEvent[],
            pagination: PaginationData,
            topMatchShown: boolean,
        ) => void;
        /**
         * Util for tracking search updates
         */
        logMetrics: (data: SearchCachedData) => void;
        /**
         * Type of individual filter to show in the filter panel
         * or '' if all filters must be shown
         */
        filterTypeToShow?: $FixMe;
        isAuthenticated?: boolean;
        showErrorNotification?: boolean;
        isOnline: boolean;
        thingsToDoShelf: ThingsToDoShelfState;
    };

function _SearchPageContent({
    locale,
    history,
    location,
    isOnline,
    eventSearch,
    searchContext,
    dispatch,
    logMetrics,
    onTrackPageView,
    isAuthenticated = false,
    showErrorNotification,
    addMainNotification,
    hideMainNotification,
    thingsToDoShelf,
}: SearchPageContentReduxProps) {
    const { events, pagination, isLoading, error } = useSearchData({
        eventSearch,
        searchContext,
        onNewData: logMetrics,
    });

    useEventSearchSyncEffects({
        eventSearch,
        dispatch,
        history,
        thingsToDoShelf,
    });
    useSearchBaseTrackingEffects({
        onTrackPageView,
        isAuthenticated,
        pagination,
        events,
        location,
    });
    useErrorHandlingEffects({
        error,
        dispatch,
        showErrorNotification,
        addMainNotification,
        hideMainNotification,
    });

    return (
        <>
            <div
                className={classNames(
                    styles.root,
                    'search-base-screen--marmalade-filters',
                )}
            >
                <section className={styles.searchPanel}>
                    <SearchResultsPanelContainer
                        events={events}
                        pagination={pagination}
                        isLoading={isLoading}
                    />
                </section>

                <section className={styles.mapPanel} data-id="search-map-panel">
                    {!isOnline && (
                        <LazyMapPanelContainer
                            events={events}
                            isOnline={isOnline}
                            isLoading={isLoading}
                        />
                    )}
                </section>
            </div>
            <RenderOnViewport
                options={{
                    rootMargin: '300px',
                }}
            >
                <SearchBasedRecommendedEvents
                    className={styles.searchBasedRecommendedEvents}
                    locale={locale}
                    isAuthenticated={isAuthenticated}
                    eventSearch={eventSearch}
                />
            </RenderOnViewport>
        </>
    );
}

const _mapStateToProps = (state: DiscoverState) => ({
    locale: state.app.locale,
    eventSearch: state.search.eventSearch,
    searchContext: {
        isStaff: state.user.isStaff,
        experimentOverride: state.admin.experimentOverride,
    },
    eventsContext: {
        section: getSearchSection(state.location.isOnline),
        currencyFormat: state.currencyFormat,
    },
    showErrorNotification: state.ui.showErrorNotification,
    thingsToDoShelf: state.thingsToDoShelf,

    //Props below for withEventSaveNotification HoC
    isAuthenticated: state.auth.isAuthenticated,
    isOnline: state.location.isOnline,
    isBotRequest: state.app.isBotRequest,
});

const _mapDispatchToProps = (dispatch: Function) => ({
    onTrackPageView: (
        events: FormattedEvent[],
        pagination: PaginationData,
        topMatchShown: boolean,
    ) => dispatch(trackSearchPageView(events, pagination, topMatchShown)),
    logMetrics: (data: SearchCachedData) =>
        dispatch(logSearchMetrics(data.metrics)),
    dispatch,
});

export const SearchPageContent: FunctionComponent = compose<$FixMe>(
    withRouter,
    withMainControls,
    connect(_mapStateToProps, _mapDispatchToProps),
)(_SearchPageContent);
